import type { CompLayout, CompRef } from 'types/documentServices';

import type { EditorAPI } from '#packages/editorAPI';
import constants from '#packages/constants';
import { array } from '#packages/util';

import type { SlotData } from '../types';
import type { SlotOpenerScope } from '../scope';

const COMP_MARGIN = 20; // margin between components after the slot was opened

const containersWithoutTransaction = [
  constants.COMP_TYPES.FORM_CONTAINER,
  constants.COMP_TYPES.MEDIA_CONTAINER,
];
/**
 * When dragging a component that is initially located below the slot and its belowComponent,
 * sometimes push api creates a huge gap. To avoid this gap, we wrap it in transaction.
 * However, in some containers, with transaction the belowComponent is not moved below enough
 */
const shouldPositionWithoutTransaction = (
  editorAPI: EditorAPI,
  selectedComps: CompRef | CompRef[],
  container: CompRef,
) => {
  const isMultiselect = array.isMultiselect(selectedComps);

  return (
    isMultiselect ||
    containersWithoutTransaction.includes(
      editorAPI.components.getType(container),
    )
  );
};
/**
 * Push components to make space for slot and position selected component in the middle
 */
export async function openSlotAndPositionComp(
  scope: SlotOpenerScope,
  selectedComp: CompRef | CompRef[],
  slot: SlotData,
) {
  const { editorAPI } = scope;
  const selectedCompLayout =
    editorAPI.components.layout.measure.getBoundingLayout(selectedComp);

  const container = editorAPI.components.getContainer(selectedComp);

  const isInFooter = !!editorAPI.sections.isFooter(
    editorAPI.sections.getClosestSectionLike(selectedComp),
  );

  const positionComponents = async () => {
    openSlot(scope, slot, container, selectedCompLayout);

    // Component will be mispositioned if it's new bottomY is below page bottomY, so we wait for the previous moveToAndPush to create enough space
    if (isInFooter) {
      await editorAPI.waitForChangesAppliedAsync();
    }

    const compPositon = getCompPosition(scope, slot, selectedCompLayout);

    editorAPI.components.layout.moveToBoundingCoordinates(
      selectedComp,
      compPositon,
      { dontAddToUndoRedoStack: true },
    );
  };

  if (shouldPositionWithoutTransaction(editorAPI, selectedComp, container)) {
    positionComponents();
  } else {
    editorAPI.transactions.run(async () => {
      positionComponents();
    });
  }
}

export function openSlot(
  { editorAPI }: SlotOpenerScope,
  slot: SlotData,
  container: CompRef,
  selectedCompLayout: CompLayout,
) {
  const containerLayout =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
      container,
    );

  const selectedCompY = slot.layout.y - containerLayout.y + COMP_MARGIN;
  const bottomCompY = selectedCompY + selectedCompLayout.height + COMP_MARGIN;

  if (slot.belowComponent) {
    editorAPI.components.layout.moveToAndPush(slot.belowComponent, {
      y: bottomCompY,
    });
  } else {
    editorAPI.components.layout.resizeToAndPush(container, {
      height:
        containerLayout.height +
        selectedCompLayout.height +
        COMP_MARGIN * 2 -
        slot.layout.height,
    });
  }
}

export function getCompPosition(
  { editorAPI }: SlotOpenerScope,
  slot: SlotData,
  selectedCompLayout: CompLayout,
) {
  const containerLayout =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
      slot.container,
    );

  const middleXRelativeToContainer =
    slot.layout.x - containerLayout.x + slot.layout.width / 2;

  return {
    x: middleXRelativeToContainer - selectedCompLayout.width / 2,
    y: slot.layout.y - containerLayout.y + COMP_MARGIN,
  };
}
